"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { getNextMintStart } from "@/components/meme-calendar/meme-calendar.helpers";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import Link from "next/link";
interface NextMintCardProps {
  readonly drop: ApiDrop;
}
const formatNextMintDateTime = (date: Date): string => {
  const dateParts = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short"
  }).formatToParts(date);
  const day = dateParts.find(p => p.type === "day")?.value ?? "";
  const month = dateParts.find(p => p.type === "month")?.value ?? "";
  const dateLabel = `${day} ${month}`;
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
  return `${dateLabel} · ${timeLabel}`;
};
export const NextMintCard = t0 => {
  const $ = _c(37);
  const {
    drop
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const isHtml = media?.mime_type === "text/html";
  let t1;
  if ($[0] !== drop.metadata || $[1] !== hasTouchScreen || $[2] !== isHtml) {
    t1 = hasTouchScreen && isHtml ? getDropPreviewImageUrl(drop.metadata) ?? undefined : undefined;
    $[0] = drop.metadata;
    $[1] = hasTouchScreen;
    $[2] = isHtml;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const htmlPreviewImageUrl = t1;
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    const nextMintDate = getNextMintStart();
    t2 = formatNextMintDateTime(nextMintDate);
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const timestamp = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/5 tw-bg-[#111111] tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2"><div className="tw-flex tw-items-center tw-gap-2"><span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" /><span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide tw-text-emerald-400">NEXT MINT</span></div><span className="tw-font-mono tw-text-xs tw-text-white/50">{timestamp}</span></div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== hasTouchScreen || $[7] !== htmlPreviewImageUrl || $[8] !== media) {
    t4 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-black/50"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{media ? <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageObjectPosition="center" imageScale={ImageScale.AUTOx600} disableAutoPlay={hasTouchScreen} disableModal={hasTouchScreen} htmlPreviewImageUrl={htmlPreviewImageUrl} /> : <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-black/40"><span className="tw-text-sm tw-text-white/40">No image</span></div>}</div></div>;
    $[6] = hasTouchScreen;
    $[7] = htmlPreviewImageUrl;
    $[8] = media;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = media?.mime_type;
  let t6;
  if ($[10] !== drop.id || $[11] !== t5) {
    t6 = <MediaTypeBadge mimeType={t5} dropId={drop.id} size="sm" />;
    $[10] = drop.id;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== drop.id || $[14] !== drop.wave.id) {
    t7 = getWaveRoute({
      waveId: drop.wave.id,
      extraParams: {
        drop: drop.id
      },
      isDirectMessage: false,
      isApp: false
    });
    $[13] = drop.id;
    $[14] = drop.wave.id;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7 || $[17] !== title) {
    t8 = <Link href={t7} className="tw-m-0 tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-text-base tw-font-semibold tw-leading-tight tw-text-white tw-no-underline tw-transition-colors group-hover:tw-text-white/80 @lg:tw-line-clamp-1">{title}</Link>;
    $[16] = t7;
    $[17] = title;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t8) {
    t9 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">{t6}{t8}</div>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = `/${author.handle ?? author.primary_address}`;
  const t11 = author.handle ?? "Artist";
  let t12;
  if ($[22] !== author.pfp || $[23] !== t11) {
    t12 = <ProfileAvatar pfpUrl={author.pfp} alt={t11} size={ProfileBadgeSize.SMALL} />;
    $[22] = author.pfp;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = author.handle ?? "Anonymous";
  let t14;
  if ($[25] !== t13) {
    t14 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t13}</span>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t10 || $[28] !== t12 || $[29] !== t14) {
    t15 = <Link href={t10} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t12}{t14}</Link>;
    $[27] = t10;
    $[28] = t12;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15 || $[32] !== t9) {
    t16 = <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-white/5 tw-px-3 tw-py-3 sm:tw-px-4">{t9}{t15}</div>;
    $[31] = t15;
    $[32] = t9;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16 || $[35] !== t4) {
    t17 = <div className="tw-group tw-flex tw-flex-col tw-text-left tw-transition-all tw-duration-300"><div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/5 tw-bg-[#0c0c0c] tw-transition-colors group-hover:tw-border-white/10">{t3}{t4}{t16}</div></div>;
    $[34] = t16;
    $[35] = t4;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
};
function _temp(m) {
  return m.data_key === "title";
}